import React, { memo, useState, Fragment, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { gql, useQuery, useMutation } from '@apollo/client'
import { CAMPAIGN_FIELDS } from '../../graphql/campaign-fragments'
import Card from '../../components/common/Card'
import CampaignForm from '../../components/campaigns/CampaignForm'
import { CubeLoader } from '@/components/ui/cube-loader'
import { Button } from '@/components/ui/button'
import { EditIcon } from '@/components/icons/new'

const GET_CAMPAIGN = gql`
    ${CAMPAIGN_FIELDS}
    query Campaigns($where: CampaignWhere) {
        campaigns(where: $where) {
            ...CampaignFields
        }
    }
`

const UPDATE_CAMPAIGN = gql`
    ${CAMPAIGN_FIELDS}
    mutation UpdateCampaign($campaignId: ID!, $campaignInput: CampaignInput) {
        updateCampaign(campaignId: $campaignId, campaignInput: $campaignInput) {
            ...CampaignFields
        }
    }
`

const EditCampaign = memo(() => {
    const params = useParams()
    const campaignId = params.id
    const [updatedData, setUpdatedData] = useState({})
    const [errors, setError] = useState([])
    const [isDirty, setIsDirty] = useState(false)
    const [isAutosaving, setIsAutosaving] = useState(false)
    const [autosaveStatus, setAutosaveStatus] = useState(null)
    const [lastSaveTime, setLastSaveTime] = useState(null)
    const [campaignSubmitAction, setCampaignSubmitAction] = useState(null)
    const [campaignSubmitDisabled, setCampaignSubmitDisabled] = useState(false)
    const [campaignSubmitLabel, setCampaignSubmitLabel] = useState('Save')
    const navigate = useNavigate()
    const autosaveTimerRef = useRef(null)

    const handleCampaignFooterStateChange = useCallback(({ onSubmit, submitDisabled, submitLabel }) => {
        setCampaignSubmitAction(() => onSubmit)
        setCampaignSubmitDisabled(submitDisabled)
        setCampaignSubmitLabel(submitLabel)
    }, [])

    const { data, loading } = useQuery(GET_CAMPAIGN, {
        variables: { where: { id: campaignId } },
    })

    // Format the timestamp
    const formatTime = (date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }

    // Autosave status message component
    const AutosaveStatus = () => {
        let message = ''
        let bgColor = ''

        switch (autosaveStatus) {
            case 'saving':
                message = 'Saving changes...'
                bgColor = 'bg-warning'
                break
            case 'saved':
                message = lastSaveTime ? `Changes saved at ${formatTime(lastSaveTime)}` : 'Changes saved'
                bgColor = 'bg-success'
                break
            case 'error':
                message = 'Error saving changes'
                bgColor = 'bg-danger'
                break
            default:
                return null
        }

        return (
            <div className={`autosave-status ${bgColor} text-white badge rounded-pill`}>
                <span>{message}</span>
            </div>
        )
    }

    const updateCampaignCallback = async () => {
        await updateCampaign({
            variables: {
                campaignId: campaignId,
                campaignInput: {
                    ...campaignInputFromData(updatedData),
                    // Saving a draft keeps it a draft. Completing one means choosing a subscription
                    // plan, which happens in the wizard reached from Complete Setup.
                    systemStatus: data?.campaigns[0]?.systemStatus,
                },
            },
        })
    }

    const saveDraftCallback = async () => {
        // Skip autosave if not enough data has been entered yet
        if (!hasMinimumRequiredData()) {
            return
        }

        setIsAutosaving(true)
        setAutosaveStatus('saving')
        try {
            await updateCampaign({
                variables: {
                    campaignId: campaignId,
                    campaignInput: {
                        ...campaignInputFromData(updatedData),
                        systemStatus:
                            data?.campaigns[0]?.systemStatus === 'DRAFT' ? 'DRAFT' : data?.campaigns[0]?.systemStatus,
                    },
                },
            })
            setIsDirty(false)
            setLastSaveTime(new Date())
            setAutosaveStatus('saved')
        } catch (err) {
            console.error('Autosave failed:', err)
            setAutosaveStatus('error')
        } finally {
            setIsAutosaving(false)
        }
    }

    // Helper function to generate campaignInput from updatedData
    const campaignInputFromData = (data) => ({
        name: data.name || 'Untitled Campaign',
        userStatus: data.userStatus || 'ACTIVE',
        notes: data.notes || '',
        purpose: data.purpose || '',
        geolocation: data.geolocation?.latitude ? data.geolocation : { latitude: 0, longitude: 0 },
        address: data.address || '',
        radius: parseInt(data.radius) || 1,
        keywords: JSON.stringify(data.keywords?.length ? data.keywords : []),
        user: {
            // Don't include empty email (use null or omit)
            ...(data.email && data.email.trim() !== '' ? { email: data.email } : {}),
            username: data.username || '',
            password: isAutosaving ? '' : data.password || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            timeZoneOffset: data.timeZoneOffset || 0,
        },
    })

    // Check if we have the minimum data needed for autosave
    const hasMinimumRequiredData = () => {
        // We only need at least one change to make it worth saving
        return isDirty
    }

    const updateData = (data) => {
        setUpdatedData(data)
        setIsDirty(true)
    }

    const [updateCampaign, mutation] = useMutation(UPDATE_CAMPAIGN, {
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
        },
        onCompleted: (data) => {
            const isDraft = data.updateCampaign.systemStatus === 'DRAFT'

            // Only navigate if not an autosave
            if (!isAutosaving) {
                navigate('/campaigns', {
                    state: {
                        notification: {
                            status: isDraft ? 'info' : 'success',
                            message: isDraft
                                ? 'Campaign draft has been updated successfully!'
                                : 'Campaign has been updated successfully!',
                        },
                    },
                })
            }
        },
    })

    // Autosave functionality - triggers only after user input has stopped for 3 seconds
    useEffect(() => {
        // Only set up autosave if data is dirty (has unsaved changes)
        if (isDirty && !isAutosaving) {
            // Clear existing timer
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current)
            }

            // Set new timer for autosave - will trigger 3 seconds after last input
            autosaveTimerRef.current = setTimeout(() => {
                saveDraftCallback()
            }, 3000)
        }

        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current)
            }
        }
    }, [updatedData]) // Only depend on updatedData - this means it only reacts to data changes

    if (loading) return <CubeLoader size="default" fullScreen={true} />

    const isDraft = data.campaigns[0].systemStatus === 'DRAFT'

    // Same destination as the Licenses List's "Complete Setup": a draft is finished in the
    // subscription wizard, where a plan is chosen and paid for.
    const goToCompleteSetup = () => navigate(`/campaigns/new?draft=${encodeURIComponent(campaignId)}`)

    return (
        <Fragment>
            <Card className="page-card">
                <Card.Header className="d-flex justify-content-between">
                    <div className="header-title">
                        <h5 className="card-title d-flex align-items-center gap-2">
                            <i className="icon">
                                <EditIcon />
                            </i>
                            Edit License - {data.campaigns[0].name}
                            {isDraft && <span className="ms-2 badge rounded-pill bg-primary">Draft</span>}
                        </h5>
                    </div>
                    <AutosaveStatus />
                </Card.Header>
                <Card.Body>
                    <div className="new-user-info">
                        <CampaignForm
                            data={data.campaigns[0]}
                            campaignId={campaignId}
                            updateData={updateData}
                            callback={updateCampaignCallback}
                            errors={errors}
                            mutation={mutation}
                            submitLabel={['Save', 'Saving…']}
                            onFooterStateChange={handleCampaignFooterStateChange}
                        />
                    </div>
                </Card.Body>
                <Card.Footer>
                    <div className="d-flex justify-content-end gap-3 py-2">
                        {isDraft && (
                            <Button variant="outline" onClick={goToCompleteSetup}>
                                Complete Setup
                            </Button>
                        )}
                        <Button
                            onClick={() => campaignSubmitAction?.()}
                            disabled={campaignSubmitDisabled}
                        >
                            {campaignSubmitLabel}
                        </Button>
                    </div>
                </Card.Footer>
            </Card>
        </Fragment>
    )
})

export default EditCampaign
